import { useEffect as useDrawerBaselineEffect } from "react";
import { useFormDirty } from "../../../../../../../shared/frontend/forms/useFormDirty";
import React from "react";
import { Button, Form, Input, Space } from "../../../../../../../shared/frontend/ui";
import { DetailField, DetailList } from "../../../../../../../shared/frontend/components/DetailList";
import { useFeedback } from "../../../../../../../shared/frontend/feedback/useFeedback";
import { useAuth } from "../../../app/auth/useAuth";
import { useI18n } from "../../../app/i18n/useI18n";
import { Permissions } from "../../../app/constants/permissions";
import { FormDrawer } from "../../../components/FormDrawer";
import { usePersonAggregatorIdentities, type AggregatorRequirement } from "../../../app/integrations/usePersonAggregatorIdentities";

/** Only used in the expanded details of a product which actually requires an aggregator. */
export function ContractAggregatorSection({ personId, requirements }: {
  personId: string; requirements: AggregatorRequirement[];
}) {
  const { text } = useI18n();
  const auth = useAuth();
  const feedback = useFeedback();
  const canUpdate = auth.hasAnyPermission(Permissions.PersonsUpdate);
  const { saveIdentity, linkWellpass } = usePersonAggregatorIdentities(personId);
  const [editing, setEditing] = React.useState<AggregatorRequirement | null>(null);
  const formId = React.useId();
  const [form] = Form.useForm<{ customerId: string; tan: string }>();
  const { dirty: drawerDirty, resetDirty: resetDrawerDirty } = useFormDirty(form);
  const drawerOpen = editing != null;
  useDrawerBaselineEffect(() => { if (drawerOpen) resetDrawerDirty(); }, [drawerOpen, resetDrawerDirty]);

  // Multiple components may require the same connection; do not repeat its editor.
  const rows = requirements.filter((row, index) => requirements.findIndex(x => x.integrationId === row.integrationId) === index);
  return <>
    <DetailList>
      {rows.map(row => <DetailField key={row.integrationId} label={row.displayName}>
        <Space wrap>
          {text(row.customerId ? "Aggregator linked" : "Aggregator identity required")}
          {canUpdate && <Button onClick={() => {
            form.resetFields(); form.setFieldValue("customerId", row.customerId ?? ""); setEditing(row);
          }}>{text(row.customerId ? "Edit" : "Link aggregator membership")}</Button>}
          {canUpdate && row.customerId && <Button loading={saveIdentity.isPending}
            onClick={() => feedback.confirmDelete({ title: text("Unlink aggregator membership?"),
              content: text("Aggregator unlink consequence"), okText: text("Unlink"), cancelText: text("Cancel"),
              onOk: async () => { await saveIdentity.mutateAsync({ provider: row.provider, customerId: "" }); }
            })}>{text("Unlink")}</Button>}
        </Space>
      </DetailField>)}
    </DetailList>
    <FormDrawer dirty={drawerDirty} title={editing?.displayName ?? ""} open={editing != null} onCancel={() => setEditing(null)}
      formId={formId} submitting={saveIdentity.isPending || linkWellpass.isPending}>
      <Form id={formId} form={form} layout="vertical" onFinish={async values => {
        if (!editing) return;
        if (editing.provider === "EgymWellpass") {
          await linkWellpass.mutateAsync({ integrationId: editing.integrationId, tan: values.tan });
        } else {
          await saveIdentity.mutateAsync({ provider: editing.provider, customerId: values.customerId });
        }
        setEditing(null); form.resetFields();
      }}>
        {editing?.provider === "EgymWellpass"
          ? <Form.Item name="tan" label={text("Wellpass TAN")} tooltip={text("Wellpass TAN source")}
              rules={[{ required: true }, { pattern: /^[0-9]{9}$/, message: text("Wellpass TAN format") }]}>
              <Input.Password maxLength={9} inputMode="numeric" autoComplete="off" data-1p-ignore />
            </Form.Item>
          : <Form.Item name="customerId" label={text("USC customer ID")} rules={[{ required: true }, { max: 100 }]}>
              <Input maxLength={100} />
            </Form.Item>}
      </Form>
    </FormDrawer>
  </>;
}
